import { createHeaders, buildApiUrl } from "./apiClient";
import { getCurrentUserId } from "./authService";
import { uploadWithSignedUrl, buildPublicUrl } from "./supabaseService";

const deriveFileName = (fileOrMeta, fallback = "upload.bin") => {
  if (!fileOrMeta) return fallback;
  const candidate = fileOrMeta.name || fileOrMeta.filename || fileOrMeta.title;
  if (candidate && typeof candidate === "string") {
    const trimmed = candidate.trim();
    if (trimmed) return trimmed;
  }
  return fallback;
};

const deriveContentType = (fileOrMeta, fallback = "application/octet-stream") =>
  (fileOrMeta && fileOrMeta.type) ||
  (fileOrMeta && fileOrMeta.mimeType) ||
  fallback;

const extractRecordId = (presigned) => {
  if (!presigned || typeof presigned !== "object") return null;
  const candidates = [
    presigned.recordId,
    presigned.record_id,
    presigned.documentId,
    presigned.document_id,
    presigned.id,
    presigned.document?.id,
    presigned.document?.documentId,
    presigned.document?.document_id,
    presigned.upload?.id,
    presigned.upload?.documentId,
    presigned.upload?.document_id,
  ];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    const value =
      typeof candidate === "number" && !Number.isNaN(candidate)
        ? candidate
        : typeof candidate === "string"
        ? candidate.trim()
        : null;
    if (value === null || value === "") continue;
    return value;
  }
  return null;
};

const parsePublicUrl = (url) => {
  const marker = "/storage/v1/object/public/";
  if (!url || typeof url !== "string") return { bucket: null, path: null };
  const idx = url.indexOf(marker);
  if (idx === -1) return { bucket: null, path: null };
  const remainder = url.slice(idx + marker.length);
  const [bucket, ...rest] = remainder.split("/");
  if (!bucket || rest.length === 0) return { bucket: null, path: null };
  return { bucket, path: rest.join("/") };
};

export const presignUpload = async (
  kind,
  fileOrMeta,
  userId = getCurrentUserId()
) => {
  const headers = createHeaders({ userId });
  const response = await fetch(buildApiUrl("/uploads/presign"), {
    method: "POST",
    headers,
    body: JSON.stringify({
      kind,
      filename: deriveFileName(fileOrMeta, `${kind || "upload"}.bin`),
      contentType: deriveContentType(fileOrMeta),
    }),
  });
  if (!response.ok) {
    let message = `Presign failed (${response.status})`;
    try {
      message += `: ${await response.text()}`;
    } catch (_) {}
    throw new Error(message);
  }
  return response.json();
};

export const uploadFileWithPresign = async ({
  kind,
  file,
  userId = getCurrentUserId(),
}) => {
  if (!file) throw new Error("Missing file for upload");
  const presigned = await presignUpload(kind, file, userId);
  let path = presigned.path || presigned.key;
  let bucket = presigned.bucket;

  if (!bucket) {
    const parsed = parsePublicUrl(presigned.publicUrl);
    bucket = parsed.bucket;
  }
  if (!bucket) {
    throw new Error("Presign response missing bucket");
  }
  if (!path) {
    const parsed = parsePublicUrl(presigned.publicUrl);
    if (!parsed.path) {
      throw new Error("Presign response missing path");
    }
    path = parsed.path;
  }

  presigned.path = path;

  if (!presigned.token || String(presigned.token).trim().length < 10) {
    throw new Error("Presign response missing valid token");
  }
  const recordId = extractRecordId(presigned);
  if (!recordId) {
    console.warn(
      "[uploadService] Presign response missing recognizable record id",
      presigned
    );
  }

  await uploadWithSignedUrl({
    bucket,
    path: presigned.path,
    token: presigned.token,
    file,
    contentType: presigned.contentType,
    upsert: typeof presigned.upsert === "boolean" ? presigned.upsert : false,
    signedUrl: presigned.signedUrl,
  });

  const publicUrl =
    presigned.publicUrl || buildPublicUrl(bucket, presigned.path);
  return {
    ...presigned,
    bucket,
    path: presigned.path,
    publicUrl,
    recordId,
  };
};
